import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    FaFileAlt,
    FaSignOutAlt,
    FaBars,
    FaTimes,
    FaUserGraduate,
    FaChartLine,
    FaCog,
    FaHistory
} from 'react-icons/fa';
import styled, { createGlobalStyle } from 'styled-components';

const Sidebar = () => {
    const location = useLocation();
    const { logout, user } = useAuth();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    const isActive = (path) => location.pathname.startsWith(path);

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const closeMobileSidebar = () => {
        setIsMobileOpen(false);
    };

    return (
        <>
            <GlobalStyles />
            {/* Mobile Hamburger Button */}
            <MobileMenuButton onClick={() => setIsMobileOpen(!isMobileOpen)}>
                <FaBars />
            </MobileMenuButton>

            <SidebarContainer
                className={`${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
            >
                <SidebarHeader>
                    {!isCollapsed && <Logo>IBM<span>Pearl</span></Logo>}
                    <ToggleButton onClick={toggleSidebar}>
                        {isCollapsed ? <FaBars /> : <FaTimes />}
                    </ToggleButton>
                </SidebarHeader>

                <SidebarNav>
                    <NavList>
                        <NavItem className={isActive('/applications') ? 'active' : ''}>
                            <StyledNavLink to="/applications" onClick={closeMobileSidebar}>
                                <FaFileAlt className="nav-icon" />
                                {!isCollapsed && <span>Applications</span>}
                                {isCollapsed && <Tooltip>Applications</Tooltip>}
                            </StyledNavLink>
                        </NavItem>

                        <NavItem className={isActive('/states') ? 'active' : ''}>
                            <StyledNavLink to="/states" onClick={closeMobileSidebar}>
                                <FaUserGraduate className="nav-icon" />
                                {!isCollapsed && <span>States</span>}
                                {isCollapsed && <Tooltip>States</Tooltip>}
                            </StyledNavLink>
                        </NavItem>

                        <NavItem className={isActive('/districts') ? 'active' : ''}>
                            <StyledNavLink to="/districts" onClick={closeMobileSidebar}>
                                <FaChartLine className="nav-icon" />
                                {!isCollapsed && <span>Districts</span>}
                                {isCollapsed && <Tooltip>Districts</Tooltip>}
                            </StyledNavLink>
                        </NavItem>

                        <NavItem className={isActive('/institutions') ? 'active' : ''}>
                            <StyledNavLink to="/institutions" onClick={closeMobileSidebar}>
                                <FaCog className="nav-icon" />
                                {!isCollapsed && <span>Institutions</span>}
                                {isCollapsed && <Tooltip>Institutions</Tooltip>}
                            </StyledNavLink>
                        </NavItem>

                        {(user?.role === 'superadmin' || user?.email === 'superadmin@ibm.com') && (
                            <NavItem className={isActive('/logs') ? 'active' : ''}>
                                <StyledNavLink to="/logs" onClick={closeMobileSidebar}>
                                    <FaHistory className="nav-icon" />
                                    {!isCollapsed && <span>Superadmin Center</span>}
                                    {isCollapsed && <Tooltip>Superadmin Center</Tooltip>}
                                </StyledNavLink>
                            </NavItem>
                        )}
                    </NavList>
                </SidebarNav>

                <SidebarFooter>
                    <LogoutButton onClick={logout}>
                        <FaSignOutAlt className="nav-icon" />
                        {!isCollapsed && <span>Logout</span>}
                        {isCollapsed && <Tooltip>Logout</Tooltip>}
                    </LogoutButton>
                </SidebarFooter>
            </SidebarContainer>

            {/* Overlay for mobile */}
            {isMobileOpen && (
                <SidebarOverlay onClick={closeMobileSidebar} />
            )}
        </>
    );
};

// Global Styles
const GlobalStyles = createGlobalStyle`
    :root {
        --primary-color: #182848;
        --primary-light: #27496d;
        --accent-color: #4cc9f0;
        --light-color: #f5f9ff;
        --dark-color: #0f1b2b;
        --success-color: #38b000;
        --danger-color: #f72585;
        --warning-color: #ffb703;
        --info-color: #00b4d8;
        
        --sidebar-width: 250px;
        --sidebar-collapsed-width: 80px;
        --sidebar-transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        --border-radius: 8px;
        --box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }
    
    * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }
    
    body {
        overflow-x: hidden;
    }
`;

// Styled Components
const MobileMenuButton = styled.button`
    display: none;
    position: fixed;
    top: 15px;
    left: 15px;
    z-index: 1000;
    background: var(--primary-color);
    color: white;
    border: none;
    padding: 10px;
    border-radius: var(--border-radius);
    cursor: pointer;
    font-size: 1.2rem;
    box-shadow: var(--box-shadow);
    transition: var(--sidebar-transition);
    
    &:hover {
        background: var(--primary-light);
        transform: scale(1.05);
    }
    
    @media (max-width: 768px) {
        display: block;
    }
`;

const SidebarContainer = styled.aside`
    width: var(--sidebar-width);
    background: linear-gradient(180deg, var(--primary-color) 0%, var(--dark-color) 100%);
    color: var(--light-color);
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: var(--sidebar-transition);
    z-index: 100;
    overflow: hidden;
    box-shadow: 2px 0 15px rgba(0, 0, 0, 0.1);
    
    &.collapsed {
        width: var(--sidebar-collapsed-width);
        
        &:hover {
            .tooltip {
                opacity: 1;
                visibility: visible;
            }
        }
    }
    
    @media (max-width: 768px) {
        transform: translateX(-100%);
        
        &.mobile-open {
            transform: translateX(0);
            box-shadow: 2px 0 15px rgba(0, 0, 0, 0.2);
        }
    }
`;

const SidebarHeader = styled.div`
    padding: 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 80px;
    flex-shrink: 0;
`;

const Logo = styled.h2`
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    white-space: nowrap;
    transition: var(--sidebar-transition);
    color: white;
    overflow: hidden;
    text-overflow: ellipsis;
    
    span {
        color: var(--accent-color);
        font-weight: 600;
    }
`;

const ToggleButton = styled.button`
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    cursor: pointer;
    font-size: 1rem;
    padding: 0.5rem;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: var(--sidebar-transition);
    flex-shrink: 0;
    
    &:hover {
        background: rgba(255, 255, 255, 0.2);
    }
`;

const SidebarNav = styled.nav`
    flex-grow: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1rem 0;
`;

const NavList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;
`;

const NavItem = styled.li`
    position: relative;
    width: 100%;
    
    &.active {
        a {
            background: var(--accent-color);
            color: var(--dark-color);
            font-weight: 500;
            
            .nav-icon {
                color: var(--dark-color);
            }
        }
    }
`;

const StyledNavLink = styled(NavLink)`
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    padding: 0.85rem 1.5rem;
    margin: 0 0.5rem;
    width: calc(100% - 1rem);
    color: white;
    text-decoration: none;
    transition: var(--sidebar-transition);
    white-space: nowrap;
    border-radius: var(--border-radius);
    position: relative;
    overflow: hidden;
    text-overflow: ellipsis;
    
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
    
    .nav-icon {
        font-size: 1.1rem;
        min-width: 24px;
        margin-right: 12px;
        transition: var(--sidebar-transition);
        flex-shrink: 0;
    }
    
    .sidebar.collapsed & {
        justify-content: center;
        padding: 0.85rem 0;
        margin: 0 0.5rem;
        width: calc(100% - 1rem);
        
        .nav-icon {
            margin-right: 0;
            font-size: 1.3rem;
        }
    }
`;

const Tooltip = styled.span`
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    background: var(--dark-color);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: var(--border-radius);
    font-size: 0.85rem;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: var(--sidebar-transition);
    pointer-events: none;
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    margin-left: 15px;
    
    &:before {
        content: '';
        position: absolute;
        left: -5px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
        border-right: 5px solid var(--dark-color);
    }
`;

const SidebarFooter = styled.div`
    padding: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
`;

const LogoutButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: none;
    cursor: pointer;
    font-weight: 500;
    border-radius: var(--border-radius);
    transition: var(--sidebar-transition);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    &:hover {
        background: var(--danger-color);
    }
    
    .nav-icon {
        font-size: 1.1rem;
        min-width: 24px;
        margin-right: 12px;
        transition: var(--sidebar-transition);
        flex-shrink: 0;
    }
    
    .sidebar.collapsed & {
        justify-content: center;
        padding: 0.75rem 0;
        
        .nav-icon {
            margin-right: 0;
            font-size: 1.3rem;
        }
    }
`;

const SidebarOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99;
    display: none;
    
    @media (max-width: 768px) {
        display: block;
    }
`;

export default Sidebar;